/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Fullscreen from "yet-another-react-lightbox/plugins/fullscreen";
import Video from "yet-another-react-lightbox/plugins/video";
import "yet-another-react-lightbox/styles.css";
import { css, useTheme } from "@emotion/react";
import PdfViewer from "../../MainComponents/Analyses/ResultComponents/PdfViewer";
import CampaignDialog from "../../MainComponents/Analyses/ResultComponents/CampaignDialog"
import YoutubePreview from "../../CommonUi/VideoThumbnail/YoutubePreview";

type MediaPreviewProps = {
  imageUrl?: string;
  originalUrl?: string;
  imgType?: string;
  description?: string;
  title?:string;
  t: (key: string) => string;
};

const MediaPreview: React.FC<MediaPreviewProps> = ({
  imageUrl,
  originalUrl,
  imgType,
  description,
  t,
}) => {
  const [openLightbox, setOpenLightbox] = useState(false);
  const [openPdf, setOpenPdf] = useState(false);
  const [openDesc, setOpenDesc] = useState(false);
  const theme = useTheme();
  const imageStyle = css`
    width: 350px;
    height: 200px;
    object-fit: cover;
    box-shadow: ${theme.shadows.shallow};
    border-radius: ${theme.radius.xs};
    margin-bottom: 12px;
    cursor: pointer;
    @media (max-width: 1200px) {
      max-width: none;
      width: 250px;
    }
  `;

  const textDescStyle = css`    
    max-width: 200px;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  `;

  const linkStyle = css`
    color: ${theme.colors.primary};
    text-decoration: none;
    font-weight: 600;
    cursor: pointer;
  `;  
 
function convertYouTubeUrlToEmbed(url: string): string | null {
  try {
    const ytRegex =
      /^(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const match = url.match(ytRegex);

    if (match && match[1]) {
      const videoId = match[1];
      return `https://www.youtube.com/embed/${videoId}`;
    }
    return null; // not a valid YouTube link
  } catch (err) {
    console.error("Invalid YouTube URL:", err);
    return null;
  }
}

 /** ---------- RENDER HELPERS ---------- **/

  // PDF Thumbnail + Modal
  const renderPdf = () => (
    <>
      <img
        src={imageUrl}
        alt="PDF Thumbnail"
        css={imageStyle}
        onClick={() => setOpenPdf(true)}
      />
      <PdfViewer isOpen={openPdf} onClose={() => setOpenPdf(false)}>
        <iframe src={originalUrl} width="100%" height="900px" title="PDF Viewer" />
      </PdfViewer>
    </>
  );

  // Image Thumbnail + Lightbox
  const renderImage = () => (
    <>
      <img
        src={imageUrl}
        alt="Image Thumbnail"
        css={imageStyle}
        onClick={() => setOpenLightbox(true)}
      />
      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={[{ src: originalUrl || "" }]}
        plugins={[Zoom, Fullscreen]}
        carousel={{ finite: true }}
        controller={{ closeOnBackdropClick: true }}
        zoom={{ maxZoomPixelRatio: 2 }}
      />
    </>
  );

  // Video Thumbnail + Lightbox
  const renderVideo = () => (
    <>
      <img
        src={imageUrl}
        alt="Video Thumbnail"
        css={imageStyle}
        onClick={() => setOpenLightbox(true)}
      />
      <Lightbox
        open={openLightbox}
        close={() => setOpenLightbox(false)}
        slides={[
          {
            type: "video",
            width: 1280,
            height: 720,
            poster: imageUrl,
            sources: [{ src: originalUrl || "", type: "video/mp4" }],
          },
        ]}
        plugins={[Zoom, Fullscreen, Video]}
        controller={{ closeOnBackdropClick: true }}
      />
    </>
  );

  // Description only
  const renderDescription = () => (
    <div css={textDescStyle}>
      {description && description.length > 200 ? (
        <>
          {description.substring(0, 197).trim()}...
          <span css={linkStyle} onClick={() => setOpenDesc(true)}>
            {t("response_collapseToggleWithPagesSeeMore")}
          </span>
        </>
      ) : (
        description
      )}
    </div>
  );

  // Description only
  const renderYoutubeUrl = () => (
     <>
       <a   onClick={() => setOpenLightbox(true)}><YoutubePreview showDes={false}  youtubeurl={originalUrl || ''} /></a>

      <PdfViewer isOpen={openLightbox} onClose={() => setOpenLightbox(false)}>
         <div style={{ width: "100%", height: "100%" }}>
          <iframe
          width="100%"
          height="100%"
        src={
            originalUrl
              ? convertYouTubeUrlToEmbed(originalUrl) ?? ""
              : ""
          }          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          ></iframe>
          </div>

      </PdfViewer>
    </>    
  );

  /** ---------- MAIN RENDER ---------- **/
  return (
    <div>
      {imageUrl ? (
         imgType === "text/uri-list"
          ? renderYoutubeUrl():
        imgType === "application/pdf"
          ? renderPdf()
          : imgType === "video/mp4"
          ? renderVideo()
          : renderImage()
      ) : (
        renderDescription()
      )}

      {/* "See description" link if media exists */}
      {imageUrl && description && (
        <div css={linkStyle} onClick={() => setOpenDesc(true)}>
          {t("analyse_result_card_title_seeDescription")}
        </div>
      )}

      {/* TODO: Your description modal can go here if needed */}
          <CampaignDialog
            open={openDesc}
            onClose={() => setOpenDesc(false)}
            campDescription={description || ""}
            title={""}
          />
    </div>
  );
};


export default MediaPreview;